import type {
  ComponentPropsWithoutRef,
  ElementType,
  ReactElement,
} from "react";
import { cn } from "@/lib/utils";

const textSizeClasses = {
  base: "text-base",
  lg: "text-lg",
  sm: "text-sm",
} as const;

const textVariantClasses = {
  prose: "text-text-paragraph md:text-lg md:text-pretty",
} as const;

const textWeightClasses = {
  medium: "font-medium",
  regular: "font-normal",
  semibold: "font-semibold",
} as const;

type TextSize = keyof typeof textSizeClasses;
type TextVariant = keyof typeof textVariantClasses;
type TextWeight = keyof typeof textWeightClasses;

interface TextOwnProps {
  className?: string;
  size?: TextSize;
  truncate?: boolean;
  variant?: TextVariant;
  weight?: TextWeight;
}

export type TextProps<E extends ElementType = "p"> = TextOwnProps & {
  as?: E;
} & Omit<ComponentPropsWithoutRef<E>, keyof TextOwnProps | "as">;

export function Text<E extends ElementType = "p">({
  as,
  size = "base",
  variant,
  weight = "regular",
  truncate,
  className,
  ...rest
}: TextProps<E>): ReactElement {
  const Component = (as ?? "p") as ElementType;

  return (
    <Component
      className={cn(
        textSizeClasses[size],
        variant && textVariantClasses[variant],
        textWeightClasses[weight],
        truncate && "truncate",
        className
      )}
      data-text=""
      {...rest}
    />
  );
}
